import { createApi, retry } from "@reduxjs/toolkit/query/react";
import http from "../http-common";
import { persistAuthHeadersInDeviceStorage, setAuthHeaders } from "./auth";
import AsyncLocalStorage from "../AsyncLocalStorage";

const Storage = AsyncLocalStorage;

export const baseQuery = () => {
  return async ({ url, method, data, params, responseType }) => {
    try {
      const response = await http({
        url: url,
        method,
        data,
        params,
        responseType,
      });

      setAuthHeaders(response.headers);
      persistAuthHeadersInDeviceStorage(Storage, response.headers);

      const meta = {};

      return { data: response.data, meta };
    } catch (axiosError) {
      let err = axiosError;

      if (
        err.response?.status === 400 ||
        err.response?.status === 403 ||
        err.response?.status === 404 ||
        err.response?.status === 422 ||
        err.response?.status === 500
      ) {
        setAuthHeaders(err.response?.headers);
        persistAuthHeadersInDeviceStorage(Storage, err.response?.headers);
      }

      return {
        error: { status: err.response?.status, data: err.response?.data },
      };
    }
  };
};

const staggeredBaseQuery = retry(
  async (args, api, extraOptions) => {
    const result = await baseQuery()(args, api, extraOptions);

    // bail out of re-tries immediately if
    // we know successive re-retries would be redundant
    if (
      result.error?.status === 400 ||
      result.error?.status === 403 ||
      result.error?.status === 404 ||
      result.error?.status === 422 ||
      result.error?.status === 500
    ) {
      retry.fail(result.error);
    }

    if (result.error?.status === 401) {
      console.log("creds failed...");
    }

    return result;
  },
  {
    maxRetries: 3,
  }
);

const fortApiTagTypes = [
  "Account",
  "AccountStats",
  "Client",
  "Comment",
  "Contact",
  "CreditPreview",
  "CreditSubmission",
  "Document",
  "Event",
  "FinancingOption",
  "OpportunityTags",
  "FinancingOptionSegment",
  "FinancingOptionTemplate",
  "Guarantor",
  "Lender",
  "Membership",
  "Tag",
  "Opportunity",
  "OpportunityContact",
  "OpportunityDocument",
  "OpportunityAttachment",
  "OpportunityEnvelope",
  "OpportunityEnvelopeTemplate",
  "RateCard",
  "Task",
  "Team",
  "Transaction",
  "SavedView",
  "Vendor",
  "VendorPayment",
  "VendorProgram",

  // ADMIN
  "AdminAccount",
  "AdminOpportunity",
  "AdminVendorProgram",

  // SANDBOX
  "PaymentLink",
  "StripeSetupIntent",
  "StripePaymentMethod",
];

export const fortApi = createApi({
  reducerPath: "fortApi",
  baseQuery: staggeredBaseQuery,

  tagTypes: fortApiTagTypes,

  endpoints: (builder) => ({
    // Client Profile

    getClientStats: builder.query({
      query: ({ account_id }) => {
        return {
          url: `/client_stats`,
          params: { account_id },
          method: "get",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "AccountStats", id }],
    }),

    // OPPORTUNITIES

    getOpportunityStats: builder.query({
      query: ({
        filters,
        task_name,
        status,
        search,
        originator,
        savedView,
      }) => {
        let url = "/opportunity_stats";

        const queries = [];
        if (filters && filters.length > 0) {
          queries.push(`filters=${filters}`);
        }
        if (search && search.length > 0) {
          queries.push(`search=${search}`);
        }
        if (originator && originator.length > 0) {
          queries.push(`originator=${originator}`);
        }
        if (savedView && savedView?.length > 0) {
          queries.push(`saved_view=${savedView}`);
        }
        if (task_name && task_name.length > 0 && status && status.length) {
          queries.push(`task_name=${task_name}`);
          queries.push(`status=${status}`);
        }
        url = url + "?";
        for (let i = 0; i < queries.length; i++) {
          url = url + queries[i];
          if (i != queries.length - 1) {
            url = url + "&";
          }
        }

        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: () => [{ type: "Opportunity", id: "LIST" }],
    }),

    getOpportunities: builder.query({
      query: ({
        page,
        limit,
        filters,
        task_name,
        status,
        sort,
        search,
        originator,
        savedView,
        forExport,
        expand = false,
      }) => {
        let url = "/opportunities";

        const queries = [];
        if (page) {
          queries.push(`page=${page}`);
        }
        if (limit) {
          queries.push(`limit=${limit}`);
        }
        if (filters && filters.length > 0) {
          queries.push(`filters=${filters}`);
        }
        if (search && search.length > 0) {
          queries.push(`search=${search}`);
        }
        if (originator && originator.length > 0) {
          queries.push(`originator=${originator}`);
        }
        if (sort) {
          queries.push(`sort=${sort}`);
        }
        if (savedView && savedView?.length > 0) {
          queries.push(`saved_view=${savedView}`);
        }
        if (forExport) {
          queries.push(`for_export=true`);
        }
        if (task_name && task_name.length > 0 && status && status.length) {
          queries.push(`task_name=${task_name}`);
          queries.push(`status=${status}`);
        }
        if (expand) {
          queries.push(`expand=${expand}`);
        }
        url = url + "?";
        for (let i = 0; i < queries.length; i++) {
          url = url + queries[i];
          if (i != queries.length - 1) {
            url = url + "&";
          }
        }

        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        if (result.opportunities.data) {
          return [
            ...(result.opportunities &&
              result.opportunities.data.map(({ id }) => ({
                type: "Opportunity",
                id,
              }))),
            { type: "Opportunity", id: "LIST" },
          ];
        } else {
          return [{ type: "Opportunity", id: "LIST" }];
        }
      },
    }),
    getOpportunity: builder.query({
      query: (id) => ({ url: `/opportunities/${id}`, method: "get" }),
      transformResponse: (response) => {
        let formattedResponse = {
          ...response.data,
          included: response.included,
        };

        return formattedResponse;
      },
      providesTags: (result, error, id) => [{ type: "Opportunity", id }],
    }),
    createOpportunity: builder.mutation({
      query: ({ clientAccountId, ...body }) => ({
        url: `/clients/${clientAccountId}/opportunities`,
        method: "post",
        data: {
          opportunity: body.opportunity,
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Opportunity", id: "LIST" }, "Task"],
    }),
    updateOpportunity: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/opportunities/${id}`,
        method: "patch",
        data: { opportunity: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["Opportunity", "Task"],
    }),
    updateOpportunityStage: builder.mutation({
      query: ({ id, stage_name }) => ({
        url: `/opportunities/${id}/${stage_name}`,
        method: "patch",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["Opportunity", "Task"],
    }),
    deleteOpportunities: builder.mutation({
      query: ({ filters, search, originator, opportunityIds }) => {
        let url = "/opportunities";
        let queries = [];

        if (opportunityIds && opportunityIds.length > 0) {
          let opportunityQuery = `opportunity_ids=`;
          for (let i = 0; i < opportunityIds.length; i++) {
            opportunityQuery = opportunityQuery + opportunityIds[i];
            if (i != opportunityIds.length - 1) {
              opportunityQuery = opportunityQuery + ",";
            }
          }
          queries.push(opportunityQuery);
        }
        url = url + "?";
        for (let i = 0; i < queries.length; i++) {
          url = url + queries[i];
          if (i != queries.length - 1) {
            url = url + "&";
          }
        }

        return { url: url, method: "delete" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        "Opportunity",
        { type: "Opportunity", id: "LIST" },
        "Task",
        "Transaction",
        { type: "Transaction", id: "LIST" },
      ],
    }),
    requestOpportunityAccess: builder.mutation({
      query: ({ id }) => ({
        url: `/opportunities/request_access`,
        method: "put",
        data: {
          id: id,
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["Opportunity"],
    }),

    exportOpportunities: builder.query({
      query: () => ({ url: `exports/opportunities`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "Opportunity", id }],
    }),

    // OPPORTUNITY ATTACHMENTS
    getOpportunityAttachments: builder.query({
      query: ({ opportunityId, isPublic = true }) => {
        return {
          url: `/opportunities/${opportunityId}/opportunity_attachments?public=${isPublic}`,
          method: "get",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "OpportunityAttachment", id })),
        { type: "OpportunityAttachment", id: "LIST" },
      ],
    }),
    createOpportunityAttachments: builder.mutation({
      query: ({ opportunityId, body }) => ({
        url: `/opportunities/${opportunityId}/opportunity_attachments`,
        method: "post",
        data: body,
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        "OpportunityAttachment",
        { type: "OpportunityAttachment", id: "LIST" },
        "Opportunity",
        "Task",
      ],
    }),
    updateOpportunityAttachment: builder.mutation({
      query: ({ opportunityId, id, patch }) => {
        return {
          url: `/opportunities/${opportunityId}/opportunity_attachments/${id}`,
          method: "patch",
          headers: {
            "content-type": "multipart/form-data",
          },
          data: { opportunity_attachment: patch },
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["OpportunityAttachment", "Opportunity", "Task"],
    }),
    purgeOpportunityAttachment: builder.mutation({
      query: ({ opportunityId, id, attachmentId, dIndex }) => {
        return {
          url: `/opportunities/${opportunityId}/opportunity_attachments/${id}/purge?attachmentId=${attachmentId}&index=${dIndex}`,
          method: "delete",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["OpportunityAttachment", "Opportunity", "Task"],
    }),
    deleteOpportunityAttachment: builder.mutation({
      query: ({ opportunityId, id }) => {
        return {
          url: `/opportunities/${opportunityId}/opportunity_attachments/${id}`,
          method: "delete",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["OpportunityAttachment", "Task"],
    }),

    // OPPORTUNITY ENVELOPE TEMPLATE
    getOpportunityEnvelopeTemplates: builder.query({
      query: ({ vendorProgramId, opportunityId = null }) => {
        return {
          url: `/vendor_programs/${vendorProgramId}/opportunity_envelope_templates`,
          params: opportunityId ? { opportunity_id: opportunityId } : {},
          method: "get",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        if (Array.isArray(result)) {
          return [
            ...result.map(({ id }) => ({
              type: "OpportunityEnvelopeTemplate",
              id,
            })),
            { type: "OpportunityEnvelopeTemplate", id: "LIST" },
            "OpportunityEnvelopeTemplate",
          ];
        } else {
          return [
            { type: "OpportunityEnvelopeTemplate", id: "LIST" },
            "OpportunityEnvelopeTemplate",
          ];
        }
      },
    }),
    getOpportunityEnvelopeTemplate: builder.query({
      query: ({ vendorProgramId, id }) => {
        return {
          url: `/vendor_programs/${vendorProgramId}/opportunity_envelope_templates/${id}`,
          method: "get",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [
        { type: "OpportunityEnvelopeTemplate", id },
      ],
    }),
    createOpportunityEnvelopeTemplate: builder.mutation({
      query: ({ vendorProgramId, body }) => ({
        url: `/vendor_programs/${vendorProgramId}/opportunity_envelope_templates`,
        method: "post",
        data: body,
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),
    updateOpportunityEnvelopeTemplate: builder.mutation({
      query: ({ vendorProgramId, id, patch }) => ({
        url: `/vendor_programs/${vendorProgramId}/opportunity_envelope_templates/${id}`,
        method: "patch",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { opportunity_envelope_templates: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["OpportunityEnvelopeTemplate", "Opportunity", "Task"],
    }),
    deleteOpportunityEnvelopeTemplate: builder.mutation({
      query: ({ vendorProgramId, id }) => {
        return {
          url: `/vendor_programs/${vendorProgramId}/opportunity_documents/${id}`,
          method: "delete",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["OpportunityEnvelopeTemplate"],
    }),
    generateOpportunityEnvelopeTemplate: builder.mutation({
      query: ({ vendorProgramId, id, body }) => ({
        url: `/vendor_programs/${vendorProgramId}/opportunity_envelope_templates/${id}/generate`,
        method: "post",
        data: body,
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),
    generateOpportunityEnvelopeTemplates: builder.mutation({
      query: ({ vendorProgramId, body }) => ({
        url: `/vendor_programs/${vendorProgramId}/opportunity_envelope_templates/generate`,
        method: "post",
        data: body,
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),

    // OPPORTUNITY EVENTS
    getOpportunityEvents: builder.query({
      query: (opportunityId) => {
        return { url: `/opportunities/${opportunityId}/events`, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Event", id })),
        { type: "Event", id: "LIST" },
      ],
    }),
    createOpportunityEvent: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/events`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { event: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Event", id: "LIST" }, "Event"],
    }),

    // COMPANIES SEARCH
    getCompaniesSearch: builder.query({
      query: ({ query, address }) => ({
        url: "/companies/search",
        method: "get",
        params: { query: query, address: address },
      }),
      transformResponse(response) {
        return response.results;
      },
    }),

    // CONTACTS
    getContact: builder.query({
      query: (id) => ({ url: `/contacts/${id}`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "Contact", id }],
    }),

    getContacts: builder.query({
      query: (accountId) => ({
        url: `/accounts/${accountId}/contacts`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        return [
          ...result.map(({ id }) => ({ type: "Contact", id })),
          { type: "Contact", id: "LIST" },
        ];
      },
    }),

    searchContacts: builder.query({
      query: ({ search }) => {
        let url = `/contacts/search`;
        if (search && search.length > 0) {
          url = url + "?search=" + search;
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: () => [{ type: "Contact", id: "LIST" }],
    }),

    fetchContact: builder.query({
      query: (email) => {
        let url = `/contacts/fetch`;
        if (email && email.length > 0) {
          url = url + "?email=" + encodeURIComponent(email);
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error) => {
        if (error) return [];
        return ["Contact", { type: "Contact", id: result?.id }];
      },
    }),

    createContact: builder.mutation({
      query: ({ accountId, teamId, isPublicCapture = false, ...body }) => ({
        url: `/accounts/${accountId}/contacts`,
        method: "post",
        data: {
          contact: body,
          team_id: teamId,
          public_capture: isPublicCapture,
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "Contact", id: "LIST" },
        "Contact",
        "Opportunity",
        "Task",
      ],
    }),

    captureContact: builder.mutation({
      // TODO - remove when customers log in
      query: ({ clientAccountId, isPublicCapture = false, ...body }) => ({
        url: `/clients/${clientAccountId}/contacts/capture`,
        method: "post",
        data: { contact: body, public_capture: isPublicCapture },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "Contact", id: "LIST" },
        "Contact",
        "Opportunity",
        "Task",
      ],
    }),

    updateContact: builder.mutation({
      query: ({ id, isPublicCapture = false, ...patch }) => ({
        url: `/contacts/${id}`,
        method: "patch",
        data: { contact: patch, public_capture: isPublicCapture }, // TODO - remove public capture when customers login
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: (result, error, { id }) => {
        if (error) return [];
        return [
          { type: "Contact", id: id },
          { type: "Contact", id: "LIST" },
          "Contact",
          "Opportunity",
          "Task",
        ];
      },
    }),

    // OPPORTUNITY CONTACTS
    getOpportunityContacts: builder.query({
      query: (opportunityId) => ({
        url: `/opportunities/${opportunityId}/contacts`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error) => {
        if (error) return [];
        return [
          ...result.map(({ id }) => ({ type: "OpportunityContact", id })),
          { type: "OpportunityContact", id: "LIST" },
        ];
      },
    }),
    createOpportunityContact: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/contacts`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: body,
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "OpportunityContact", id: "LIST" }],
    }),
    updateOpportunityContact: builder.mutation({
      query: ({ opportunityId, id, ...patch }) => ({
        url: `/opportunities/${opportunityId}/contacts/${id}`,
        method: "patch",
        data: { opportunity_contact: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }, error, { opportunityId }) => {
        return [
          { type: "OpportunityContact", id: id },
          { type: "OpportunityContact", id: "LIST" },
          { type: "Opportunity", id: opportunityId },
          "Task",
        ];
      },
    }),
    deleteOpportunityContacts: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/contacts`,
        method: "DELETE",
        data: body,
      }),
      invalidatesTags: (result, error, id) => [
        { type: "OpportunityContact", id },
        { type: "OpportunityContact", id: "LIST" },
        "OpportunityContact",
      ],
    }),
    captureOpportunityContact: builder.mutation({
      // TODO - remove when customers log in
      query: ({ opportunityId, isPublicCapture = false, ...body }) => ({
        url: `/opportunities/${opportunityId}/contacts/capture`,
        method: "post",
        data: { contact: body, public_capture: isPublicCapture },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "Contact", id: "LIST" },
        "Contact",
        "Opportunity",
        "OpportunityContact",
        "Task",
      ],
    }),

    // OPPORTUNITY ORIGINATOR
    searchOpportunityOriginators: builder.query({
      query: ({ opportunityId, search }) => {
        let url = `/opportunities/${opportunityId}/originator/search`;
        if (search && search.length > 0) {
          url = url + "?search=" + search;
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, { opportunityId }) => [
        { type: "Contact", id: "LIST" },
        { type: "Contact", id: `OPPORTUNITY_${opportunityId}` }
      ],
    }),
    updateOpportunityOriginator: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/originator`,
        method: "put",
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: (response, error, { opportunityId }) => {
        return [
          { type: "Opportunity", id: opportunityId }, 
          { type: "Contact", id: `OPPORTUNITY_${opportunityId}` },
          "Task"
        ];
      },
    }),

    createDesignatedSigner: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/designated_signers`,
        method: "post",
        data: { designated_signer: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: (response, error, { opportunityId }) => {
        return [
          "Opportunity",
          { type: "Opportunity", id: opportunityId },
          "OpportunityContact",
          "Task",
        ];
      },
    }),

    // ACCOUNTS
    getAccount: builder.query({
      query: (id) => ({ url: `/accounts/${id}`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "Account", id }],
    }),
    getAccounts: builder.query({
      query: () => ({ url: `/accounts`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Account", id })),
        { type: "Account", id: "LIST" },
      ],
    }),
    // AccountsController#create runs find_or_initialize on the new account
    createAccount: builder.mutation({
      query: (body) => ({
        url: "/accounts",
        method: "post",
        data: { account: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: () => [
        { type: "AdminAccount", id: "LIST" },
        "AdminAccount",
        { type: "Account", id: "LIST" },
        "Account",
        { type: "Client", id: "LIST" },
        "Client",
      ],
    }),
    updateAccount: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/accounts/${id}`,
        method: "patch",
        data: { account: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          { type: "Account", id: id },
          { type: "Account", id: "LIST" },
        ];
      },
    }),

    getAccountEvents: builder.query({
      query: ({ accountId }) => {
        return { url: `/accounts/${accountId}/events`, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Event", id })),
        { type: "Event", id: "LIST" },
      ],
    }),

    // CLIENT
    getClient: builder.query({
      query: (id) => ({ url: `/clients/${id}`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "Client", id }],
    }),
    getClients: builder.query({
      query: () => ({ url: `/clients`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Client", id })),
        { type: "Client", id: "LIST" },
      ],
    }),

    // CLIENT OPPORTUNITIES
    getClientOpportunities: builder.query({
      query: ({ clientId, page, limit, filters, sort, search }) => {
        let url = `/clients/${clientId}/opportunities`;
        let queries = [];
        if (page) {
          queries.push(`page=${page}`);
        }
        if (limit) {
          queries.push(`limit=${limit}`);
        }
        if (filters && filters.length > 0) {
          queries.push(`filters=${filters}`);
        }
        if (search && search.length > 0) {
          queries.push(`search=${search}`);
        }
        if (sort) {
          queries.push(`sort=${sort}`);
        }
        url = url + "?";
        for (let i = 0; i < queries.length; i++) {
          url = url + queries[i];
          if (i != queries.length - 1) {
            url = url + "&";
          }
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "Opportunity", id }],
    }),

    // CLIENT ACCOUNTS
    getClientAccount: builder.query({
      query: (id) => ({ url: `/clients/${id}`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "Account", id }],
    }),
    getClientAccounts: builder.query({
      query: () => ({ url: `/clients`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Account", id })),
        { type: "Account", id: "LIST" },
      ],
    }),
    updateClientAccount: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/clients/${id}`,
        method: "patch",
        data: { client: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          { type: "Account", id: id },
          { type: "Account", id: "LIST" },
        ];
      },
    }),

    // TEAM ACCOUNTS
    getTeamAccounts: builder.query({
      query: (team_id) => ({
        url: `/teams/${team_id}/accounts`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Account", id })),
        { type: "Account", id: "LIST" },
      ],
    }),

    // TEAM CLIENTS
    getTeamClients: builder.query({
      query: (team_id) => ({
        url: `/teams/${team_id}/clients`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Client", id })),
        { type: "Client", id: "LIST" },
      ],
    }),
    createClient: builder.mutation({
      query: ({ teamId, ...body }) => ({
        url: `/teams/${teamId}/clients`,
        method: "post",
        data: { client: body },
      }),
      transformResponse: (response) => {
        response.data.meta = response.meta;
        return response.data;
      },
      invalidatesTags: [{ type: "Client", id: "LIST" }, "Client"],
    }),
    updateClient: builder.mutation({
      query: ({ id, teamId, ...patch }) => ({
        url: `/teams/${teamId}/clients/${id}`,
        method: "patch",
        data: { client: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          { type: "Account", id: id },
          { type: "Account", id: "LIST" },
          { type: "Client", id: id },
          { type: "Client", id: "LIST" },
        ];
      },
    }),

    // TEAM CONTACTS
    createTeamContact: builder.mutation({
      query: ({ teamId, ...body }) => ({
        url: `/teams/${teamId}/contacts/create_invite`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: body,
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Membership", id: "LIST" }, "Membership"],
    }),

    searchTeamContacts: builder.query({
      query: ({ teamId, search }) => {
        let url = `/teams/${teamId}/contacts/search`;
        if (search && search.length > 0) {
          url = url + "?search=" + search;
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [{ type: "Contact", id: "LIST" }],
    }),

    // ACCOUNT TEAMS
    getAccountTeams: builder.query({
      query: (account_id) => ({
        url: `/accounts/${account_id}/teams`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Team", id })),
        { type: "Team", id: "LIST" },
      ],
    }),

    // TEAMS
    getTeams: builder.query({
      query: ({ contactId = null, is_home_index } = {}) => ({
        url: `/teams`,
        method: "get",
        params: contactId ? { contact_id: contactId } : { is_home_index },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Team", id })),
        { type: "Team", id: "LIST" },
      ],
    }),

    getTeam: builder.query({
      query: ({ id }) => ({
        url: `/teams/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, { id }) => [{ type: "Team", id }],
    }),

    updateTeam: builder.mutation({
      query: ({ team_id, ...body }) => ({
        url: `/teams/${team_id}`,
        method: "patch",
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Team", id: "LIST" }, "Team"],
    }),

    // FINANCING OPTIONS
    getFinancingOptions: builder.query({
      query: (opportunity_id) => ({
        url: `/opportunities/${opportunity_id}/financing_options`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "FinancingOption", id })),
        { type: "FinancingOption", id: "LIST" },
      ],
    }),
    getFinancingOption: builder.query({
      query: ({ id, opportunityId }) => ({
        url: `/opportunities/${opportunityId}/financing_options/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "FinancingOption", id }],
    }),
    getNewFinancingOption: builder.query({
      query: ({ opportunityId }) => ({
        url: `/opportunities/${opportunityId}/financing_options/new`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),
    createFinancingOption: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/financing_options`,
        method: "post",
        data: { financing_option: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "FinancingOption", id: "LIST" },
        "Opportunity",
        "Task",
      ],
    }),
    updateFinancingOption: builder.mutation({
      query: ({ id, opportunityId, ...patch }) => ({
        url: `/opportunities/${opportunityId}/financing_options/${id}`,
        method: "patch",
        data: { financing_option: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: (id) => [
        { type: "FinancingOption", id: "LIST" },
        { type: "FinancingOptionSegment", id: "LIST" },
        { type: "Document", id: "LIST" },
        "Opportunity",
        "OpportunityEnvelopeTemplate",
        "FinancingOption",
        "FinancingOptionSegment",
        { type: "OpportunityEnvelopeTemplate", id: "LIST" },
        { type: "FinancingOption", id: id },
        "Task",
        "StripeSetupIntent",
        "StripePaymentMethod",
        "Transaction",
        { type: "Transaction", id: "LIST" },
      ],
    }),
    deleteFinancingOption: builder.mutation({
      query: ({ id, opportunityId }) => ({
        url: `/opportunities/${opportunityId}/financing_options/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "FinancingOption", id },
        { type: "FinancingOption", id: "LIST" },
        "Opportunity",
        "Task",
      ],
    }),

    // FINANCING OPTION EVENTS
    createFinancingOptionEvent: builder.mutation({
      query: ({ financingOptionId, ...body }) => ({
        url: `/financing_options/${financingOptionId}/events`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { event: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Event", id: "LIST" }, "Event"],
    }),

    // FINANCING OPTION SEGMENTS
    // getFinancingOptionSegments: builder.query<
    //   FinancingOptionSegment[],
    //   string
    // >({
    getFinancingOptionSegments: builder.query({
      query: (financing_option_id) => ({
        url: `/financing_options/${financing_option_id}/financing_option_segments`,
        method: "get",
      }),
      // transformResponse: (response: {
      //   data: { financingOptionSegment: FinancingOptionSegment[] };
      // }) => {
      //   return response.data;
      // },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "FinancingOptionSegment",
                id,
              })),
              { type: "FinancingOptionSegment", id: "LIST" },
            ]
          : [{ type: "FinancingOptionSegment", id: "LIST" }],
    }),

    // FINANCING OPTION TEMPLATES
    getFinancingOptionTemplates: builder.query({
      query: (opportunityId) => ({
        url: `/opportunities/${opportunityId}/financing_option_templates`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "FinancingOptionTemplate", id })),
        { type: "FinancingOptionTemplate", id: "LIST" },
      ],
    }),
    createFinancingOptionTemplate: builder.mutation({
      query: ({ id, segments, ...body }) => ({
        url: `/admin/vendor_programs/${id}/financing_option_templates`,
        method: "post",
        data: {
          vendor_program_id: id,
          financing_option_template: body,
          segments: segments,
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          { type: "FinancingOptionTemplate", id: id },
          { type: "FinancingOptionTemplate", id: "LIST" },
        ];
      },
    }),

    updateFinancingOptionTemplate: builder.mutation({
      query: ({ id, segments, financing_option_template_id, ...body }) => ({
        url: `/admin/vendor_programs/${id}/financing_option_templates/${id}`,
        method: "patch",
        data: {
          financing_option_template_id: financing_option_template_id,
          financing_option_template: {
            ...body,
            segments: segments,
          },
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          { type: "FinancingOptionTemplate", id: id },
          { type: "FinancingOptionTemplate", id: "LIST" },
        ];
      },
    }),

    // ADMIN CREDIT SUBMISSIONn
    getAdminCreditSubmissions: builder.query({
      query: ({
        clientIdFilter,
        clientShowPage,
        lender,
        page,
        limit,
        search,
      }) => {
        let url = `/admin/credit_submissions`;
        let queries = [];

        if (clientIdFilter) {
          queries.push(`clientId=${clientIdFilter}`);
        }

        if (clientShowPage) {
          queries.push(`clientShowPage=true`);
        }

        if (lender) {
          queries.push(`lenderIds=${lender}`);
        }

        if (page) {
          queries.push(`page=${page}`);
        }
        if (limit) {
          queries.push(`limit=${limit}`);
        }
        // if (filters && filters.length > 0) {
        //   queries.push(`filters=${filters}`);
        // }
        if (search && search.length > 0) {
          queries.push(`search=${search}`);
        }
        // if (sort) {
        //   queries.push(`sort=${sort}`);
        // }
        if (queries.length > 0) {
          url = `${url}?`;
          url += queries.join("&");
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        if (result.credit_submissions.data) {
          return [
            ...(result.credit_submissions &&
              result.credit_submissions.data.map(({ id }) => ({
                type: "CreditSubmission",
                id,
              }))),
            { type: "CreditSubmission", id: "LIST" },
          ];
        } else {
          return [{ type: "CreditSubmission", id: "LIST" }];
        }
      },
    }),

    // OPPORTUNITY CREDIT SUBMISSIONS
    getOpportunityCreditSubmissions: builder.query({
      query: (opportunity_id) => ({
        url: `/opportunities/${opportunity_id}/credit_submissions`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "CreditSubmission", id })),
        { type: "CreditSubmission", id: "LIST" },
      ],
    }),
    getOpportunityCreditSubmission: builder.query({
      query: ({ id, opportunityId }) => ({
        url: `/opportunities/${opportunityId}/credit_submissions/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "CreditSubmission", id }],
    }),
    createOpportunityCreditSubmission: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/credit_submissions`,
        method: "post",
        data: { credit_submission: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "CreditSubmission", id: "LIST" },
        "Opportunity",
        "Task",
      ],
    }),
    updateOpportunityCreditSubmission: builder.mutation({
      query: ({ id, opportunityId, ...patch }) => ({
        url: `/opportunities/${opportunityId}/credit_submissions/${id}`,
        method: "patch",
        data: { credit_submission: patch },
      }),
      invalidatesTags: ({ id }) => [
        { type: "CreditSubmission", id: id },
        { type: "CreditSubmission", id: "LIST" },
        "Opportunity",
        "Task",
      ],
    }),
    deleteOpportunityCreditSubmission: builder.mutation({
      query: ({ id, opportunityId }) => ({
        url: `/opportunities/${opportunityId}/credit_submissions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "CreditSubmission", id },
        { type: "CreditSubmission", id: "LIST" },
        "Opportunity",
        "Task",
      ],
    }),
    updateOpportunityCreditSubmissionSubmitToLender: builder.mutation({
      query: ({ id, opportunityId, ssn, body }) => ({
        url: `/opportunities/${opportunityId}/credit_submissions/${id}/submit`,
        method: "post",
        data: { ssn: ssn, body: body },
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: (response) => {
        let invalidate = [
          { type: "CreditSubmission", id: "LIST" },
          "Opportunity",
          "Task",
        ];

        if (response && response?.id) {
          invalidate.push({ type: "CreditSubmission", id: response.id });
        }

        return invalidate;
      },
    }),
    updateOpportunityCreditSubmissionSelectLender: builder.mutation({
      query: ({ id, opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/credit_submissions/${id}/select`,
        method: "patch",
        data: { credit_submission: body },
      }),
      invalidatesTags: ({ id }) => [
        { type: "CreditSubmission", id: id },
        { type: "CreditSubmission", id: "LIST" },
        "Opportunity",
        "Task",
      ],
    }),
    updateOpportunityCreditSubmissionDeselectLender: builder.mutation({
      query: ({ id, opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/credit_submissions/${id}/deselect`,
        method: "patch",
        data: { credit_submission: body },
      }),
      invalidatesTags: ({ id }) => [
        { type: "CreditSubmission", id: id },
        { type: "CreditSubmission", id: "LIST" },
        "Opportunity",
        "Task",
      ],
    }),

    // VENDORS
    getVendors: builder.query({
      query: ({ filter = null, search, is_home_index } = {}) => ({
        url: `/vendors`,
        method: "get",
        params: { filter, search, is_home_index },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Vendor", id })),
        { type: "Vendor", id: "LIST" },
      ],
    }),
    createVendor: builder.mutation({
      query: (body) => ({
        url: "/vendors",
        method: "post",
        data: { vendor: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "AdminAccount", id: "LIST" },
        "AdminAccount",
        { type: "Account", id: "LIST" },
        "Account",
        { type: "Vendor", id: "LIST" },
        "Vendor",
      ],
    }),
    updateVendor: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/vendors/${id}`,
        method: "patch",
        data: { vendor: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "AdminAccount", id: "LIST" },
        "AdminAccount",
        { type: "Account", id: "LIST" },
        "Account",
        { type: "Vendor", id: "LIST" },
        "Vendor",
      ],
    }),

    // VENDOR PAYMENTS
    getVendorPayments: builder.query({
      query: ({ opportunityId, finalInvoice }) => {
        let url = `/opportunities/${opportunityId}/vendor_payments`;

        if (finalInvoice) {
          url = `${url}?final_invoice=true`;
        }
        return {
          url: url,
          method: "get",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "VendorPayment", id })),
        { type: "VendorPayment", id: "LIST" },
      ],
    }),
    getVendorPayment: builder.query({
      query: ({ id, opportunityId }) => ({
        url: `/opportunities/${opportunityId}/vendor_payments/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "VendorPayment", id }],
    }),
    createVendorPayment: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/vendor_payments`,
        method: "post",
        data: { vendor_payment: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "VendorPayment", id: "LIST" },
        "Opportunity",
        "FinancingOption",
        "Transaction",
        "Task",
        { type: "Transaction", id: "LIST" },
      ],
    }),
    updateVendorPayment: builder.mutation({
      query: ({ id, opportunityId, ...patch }) => ({
        url: `/opportunities/${opportunityId}/vendor_payments/${id}`,
        method: "patch",
        data: { vendor_payment: patch },
      }),
      invalidatesTags: (result) => {
        return [
          "FinancingOption",
          "Opportunity",
          "Task",
          "VendorPayment",
          { type: "VendorPayment", id: result?.data?.id },
          { type: "VendorPayment", id: "LIST" },
          { type: "Transaction", id: "LIST" },
          "Transaction",
        ];
      },
    }),
    deleteVendorPayment: builder.mutation({
      query: ({ id, opportunityId }) => ({
        url: `/opportunities/${opportunityId}/vendor_payments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "VendorPayment", id },
        { type: "VendorPayment", id: "LIST" },
        { type: "FinancingOption", id: "LIST" },
        "VendorPayment",
        "FinancingOption",
        "Opportunity",
        "Task",
      ],
    }),

    // VENDOR PAYMENT ATTACHMENTS
    getVendorPaymentAttachment: builder.query({
      query: ({ vendorPaymentId }) => ({
        url: `/vendor_payments/attachments/${vendorPaymentId}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "VendorPayment", id }],
    }),
    createVendorPaymentAttachment: builder.mutation({
      query: ({ body, finalInvoice }) => ({
        url: `/vendor_payments/attachments`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { vendor_payment: body, final_invoice: finalInvoice },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          { type: "VendorPayment", id: id },
          { type: "VendorPayment", id: "LIST" },
          { type: "Opportunity", id: "LIST" },
          "Opportunity",
          "Task",
          "VendorPayment",
        ];
      },
    }),
    generateVendorPayment: builder.mutation({
      query: ({ ...body }) => ({
        url: `/vendor_payments/generate`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          { type: "VendorPayment", id: id },
          { type: "VendorPayment", id: "LIST" },
          "VendorPayment",
        ];
      },
    }),

    // VENDOR PAYMENT CONFIRMATIONS
    createVendorPaymentConfirmation: builder.mutation({
      query: ({ vendorPaymentId, ...body }) => ({
        url: `/vendor_payments/${vendorPaymentId}/confirmations`,
        method: "post",
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [],
    }),

    // VENDOR PAYMENT OPPORTUNITIES
    createVendorPaymentOpportunity: builder.mutation({
      query: ({ vendorPaymentId, ...body }) => ({
        url: `/vendor_payments/${vendorPaymentId}/opportunities`,
        method: "post",
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["Contact", { type: "Opportunity", id: "LIST" }, "Task"],
    }),

    // VENDOR PROGRAM
    getVendorProgram: builder.query({
      query: ({ id }) => ({
        url: `/vendor_programs/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "VendorProgram", id: id }],
    }),
    getVendorPrograms: builder.query({
      query: () => ({
        url: `/vendor_programs/`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: () => [{ type: "VendorProgram", id: "LIST" }],
    }),

    // Docusign Embedded Signing
    createDocusignEmbeddedSigning: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/embedded_signing`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { ...body, opportunity_id: opportunityId },
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Opportunity", "Task"],
    }),
    getDocusignEmbeddedSigningStatus: builder.query({
      query: ({ opportunityId }) => ({
        url: `/signing_status?opportunity_id=${opportunityId}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Opportunity", "Task"],
    }),
    createDocusignEmbeddedSending: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/embedded_sending`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { ...body, opportunity_id: opportunityId },
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Opportunity", "Task"],
    }),
    getDocusignPdfsForSigning: builder.query({
      query: ({ opportunityId }) => ({
        url: `/pdfs?opportunity_id=${opportunityId}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Opportunity", "Task"],
    }),
    getDocusignRegeneratedEmbeddedSendingUrl: builder.query({
      query: ({ envelopeId, opportunityId }) => ({
        url: `/embedded_sending/regenerate?opportunity_id=${opportunityId}&envelope_id=${envelopeId}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Opportunity", "Task"],
    }),

    getDocusignSigningUrl: builder.query({
      query: ({ opportunityId, envelopeId }) => ({
        url: `/embedded_signing/regenerate?opportunity_id=${opportunityId}&envelope_id=${envelopeId}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Opportunity", "Task"],
    }),

    // LENDERS
    getLenders: builder.query({
      query: () => ({ url: `/lenders`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Lender", id })),
        { type: "Lender", id: "LIST" },
      ],
    }),
    createLender: builder.mutation({
      query: (body) => ({
        url: "/lenders",
        method: "post",
        data: { lender: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "AdminAccount", id: "LIST" },
        "AdminAccount",
        { type: "Account", id: "LIST" },
        "Account",
        { type: "Lender", id: "LIST" },
        "Lender",
      ],
    }),

    // DOCUMENTS
    getOpportunityDocuments: builder.query({
      query: (opportunity_id) => ({
        url: `/opportunities/${opportunity_id}/documents`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Document", id })),
        { type: "Document", id: "LIST" },
      ],
    }),

    getDocuments: builder.query({
      query: ({ search }) => {
        let url = `/documents`;
        if (search && search?.length > 0) {
          url = url + "?search=" + search;
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Document", id })),
        { type: "Document", id: "LIST" },
      ],
    }),

    getDocument: builder.query({
      query: ({ id }) => ({
        url: `/documents/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "Document", id }],
    }),

    createDocument: builder.mutation({
      query: ({ body }) => {
        return {
          url: `/documents`,
          method: "post",
          data: { document: body },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Document"],
    }),

    updateDocument: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/documents/${id}`,
        method: "patch",
        data: { document: patch, document_id: id },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          { type: "Document", id: id },
          { type: "Document", id: "LIST" },
        ];
      },
    }),

    previewDocument: builder.mutation({
      query: ({ id, body }) => ({
        url: `/documents/${id}/preview`,
        method: "post",
        data: { document: body },
      }),
      transformResponse: (response) => {
        return response;
      },
    }),

    // GUARANTORS

    getGuarantors: builder.query({
      query: (opportunity_id) => ({
        url: `/opportunities/${opportunity_id}/guarantors`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Guarantor", id })),
        { type: "Guarantor", id: "LIST" },
      ],
    }),
    getGuarantor: builder.query({
      query: ({ id, opportunityId }) => ({
        url: `/opportunities/${opportunityId}/guarantors/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "Guarantor", id }],
    }),
    searchGuarantors: builder.query({
      query: ({ opportunity_id, search }) => {
        let url = `/opportunities/${opportunity_id}/guarantors/search`;
        if (search && search.length > 0) {
          url = url + "?search=" + search;
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response;
      },
      providesTags: (result) => [
        { type: "Account", id: "LIST" },
        { type: "Contact", id: "LIST" },
      ],
    }),
    createGuarantor: builder.mutation({
      query: ({ opportunityId, body }) => {
        return {
          url: `/opportunities/${opportunityId}/guarantors/`,
          method: "post",
          data: { guarantor: body, opportunity_id: opportunityId },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => [
        "Guarantor",
        "OpportunityAttachment",
        { type: "OpportunityAttachment", id: "LIST" },
      ],
    }),
    removeGuarantors: builder.mutation({
      query: ({ opportunityId, body }) => {
        return {
          url: `/opportunities/${opportunityId}/guarantors/remove`,
          method: "delete",
          data: { body: body, opportunity_id: opportunityId },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: ["Guarantor", "OpportunityContact"],
    }),

    // TRANSACTIONS

    getTransactions: builder.query({
      query: ({ page, limit, status, search, sort }) => {
        let url = "/transactions";

        let queries = [];
        if (page) {
          queries.push(`page=${page}`);
        }
        if (limit) {
          queries.push(`limit=${limit}`);
        }
        if (status) {
          queries.push(`status=${status}`);
        }
        if (search) {
          queries.push(`search=${search}`);
        }
        if (sort) {
          queries.push(`sort=${sort}`);
        }

        url = queries.length > 0 ? `${url}?${queries.join("&")}` : url;
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        if (result.transactions.data) {
          return [
            ...(result.transactions &&
              result.transactions.data.map(({ id }) => ({
                type: "Transaction",
                id,
              }))),
            { type: "Transaction", id: "LIST" },
          ];
        } else {
          return [{ type: "Transaction", id: "LIST" }];
        }
      },
    }),

    // OPPORTUNITY TRANSACTIONS

    getOpportunityTransactions: builder.query({
      query: (opportunity_id) => ({
        url: `/opportunities/${opportunity_id}/transactions`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Transaction", id })),
        { type: "Transaction", id: "LIST" },
      ],
    }),
    getOpportunityTransaction: builder.query({
      query: ({ id, opportunityId }) => ({
        url: `/opportunities/${opportunityId}/transactions/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "Transaction", id }],
    }),
    createOpportunityTransaction: builder.mutation({
      query: ({ opportunityId, ...body }) => {
        return {
          url: `/opportunities/${opportunityId}/transactions/`,
          method: "post",
          data: { transaction: body, opportunity_id: opportunityId },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => [
        "Opportunity",
        "Task",
        "Transaction",
        { type: "Transaction", id: "LIST" },
      ],
    }),
    updateOpportunityTransaction: builder.mutation({
      query: ({ opportunityId, id, ...patch }) => ({
        url: `/opportunities/${opportunityId}/transactions/${id}`,
        method: "patch",
        data: { transaction: patch, opportunity_id: opportunityId },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id, attributes: { financing_option_id } }) => {
        return [
          "Opportunity",
          "Task",
          { type: "Transaction", id: id },
          { type: "Transaction", id: "LIST" },
          ...(financing_option_id !== null
            ? [{ type: "FinancingOption", id: financing_option_id }]
            : []),
        ];
      },
    }),
    updateOpportunityTransactions: builder.mutation({
      query: ({ opportunityId, transactionIds, ...patch }) => {
        return {
          url: `/opportunities/${opportunityId}/transactions/bulk_update`,
          method: "patch",
          data: {
            patch: patch,
            opportunity_id: opportunityId,
            transaction_ids: transactionIds,
          },
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Transaction", id: id })),
        { type: "Transaction", id: "LIST" },
        "Transaction",
        "Opportunity",
        "Task",
      ],
    }),
    deleteOpportunityTransaction: builder.mutation({
      query: ({ opportunityId, id, body }) => {
        return {
          url: `/opportunities/${opportunityId}/transactions/${id}`,
          method: "delete",
          data: { body: body, opportunity_id: opportunityId },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: ["Transaction", "Opportunity", "Task"],
    }),
    deleteOpportunityTransactions: builder.mutation({
      query: ({ opportunityId, body }) => {
        return {
          url: `/opportunities/${opportunityId}/transactions/bulk_destroy`,
          method: "delete",
          data: { body: body, opportunity_id: opportunityId },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: [
        "Opportunity",
        "Transaction",
        { type: "Transaction", id: "LIST" },
        "Task",
      ],
    }),
    scheduleOpportunityTransactions: builder.mutation({
      query: ({ opportunityId }) => {
        return {
          url: `/opportunities/${opportunityId}/transactions/schedule`,
          method: "post",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: (result) => [
        ...(result?.map(({ id }) => ({ type: "Transaction", id })) ?? []),
        { type: "Transaction", id: "LIST" },
        "Transaction",
        "Opportunity",
        "Task",
      ],
    }),

    // OPPORTUNITY TASKS
    getOpportunityTasks: builder.query({
      query: (opportunity_id) => ({
        url: `/opportunities/${opportunity_id}/tasks`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "Task",
                id,
              })),
              { type: "Task", id: "LIST" },
            ]
          : [{ type: "Task", id: "LIST" }],
    }),

    // OPPORTUNITY TAGS
    getOpportunityTags: builder.query({
      query: (opportunity_id) => ({
        url: `/opportunities/${opportunity_id}/tags`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, opportunity_id) => [
        "Tag",
        { type: "OpportunityTags", id: opportunity_id },
      ],
    }),
    updateOpportunityTags: builder.mutation({
      query: ({ opportunityId, tags }) => {
        return {
          url: `/opportunities/${opportunityId}/tags`,
          method: "post",
          data: {
            opportunity_id: opportunityId,
            tags: tags,
          },
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => [
        { type: "Opportunity", id: id },
        { type: "Opportunity", id: "LIST" },
        { type: "AdminOpportunity", id: "LIST" },
        "AdminOpportunity",
        "Opportunity",
        "Tag",
      ],
    }),

    // OPPORTUNITY DOWN PAYMENTS

    // TODO: no longer in use - remove
    //
    // createOpportunityDownPayment: builder.mutation({
    //   query: ({ opportunityId, ...body }) => {
    //     return {
    //       url: `/opportunities/${opportunityId}/down_payments/`,
    //       method: "post",
    //       data: { down_payment: body, opportunity_id: opportunityId },
    //     };
    //   },
    //   transformResponse: (response) => {
    //     return response;
    //   },
    //   invalidatesTags: () => ["Transaction"],
    // }),

    // OPPORTUNITY PAYMENT AUTHORIZATIONS

    createOpportunityPaymentAuthorization: builder.mutation({
      query: ({ opportunityId, ...body }) => {
        return {
          url: `/opportunities/${opportunityId}/payment_authorizations/`,
          method: "post",
          data: { payment_authorization: body, opportunity_id: opportunityId },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Opportunity", "Transaction", "Task"],
    }),

    // OPPORTUNITY (DOCUSIGN) ENVELOPES

    getDocusignEnvelopes: builder.query({
      query: (opportunity_id) => ({
        url: `/opportunities/${opportunity_id}/opportunity_envelopes`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "OpportunityEnvelope", id })),
        { type: "OpportunityEnvelope", id: "LIST" },
      ],
    }),
    getDocusignEnvelope: builder.query({
      query: ({ id, opportunityId }) => ({
        url: `/opportunities/${opportunityId}/opportunity_envelopes/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [
        { type: "OpportunityEnvelope", id },
      ],
    }),
    createDocusignEnvelope: builder.mutation({
      query: ({ opportunityId, body }) => {
        return {
          url: `/opportunities/${opportunityId}/opportunity_envelopes/`,
          method: "post",
          data: { body: body, opportunity_id: opportunityId },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["OpportunityEnvelope", "Opportunity", "Task"],
    }),
    updateDocusignEnvelope: builder.mutation({
      query: ({ id, opportunityId, ...patch }) => ({
        url: `/opportunities/${opportunityId}/opportunity_envelopes/${id}`,
        method: "patch",
        data: { opportunity_envelope: patch },
      }),
      invalidatesTags: ({ id }) => [
        "Opportunity",
        { type: "OpportunityEnvelope", id: id },
        { type: "OpportunityEnvelope", id: "LIST" },
        "Task",
      ],
    }),
    deleteDocusignEnvelope: builder.mutation({
      query: ({ id, opportunityId, body }) => {
        return {
          url: `/opportunities/${opportunityId}/opportunity_envelopes/${id}`,
          method: "delete",
          data: { body: body, opportunity_id: opportunityId },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: ["OpportunityEnvelope", "Opportunity", "Task"],
    }),

    // COMMENTS

    getComments: builder.query({
      query: ({ commentableId, comments, activity }) => ({
        url: `/commentables/${commentableId}/comments?comments=${comments}&activity=${activity}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Comment", id })),
        { type: "Comment", id: "LIST" },
      ],
    }),
    createComment: builder.mutation({
      query: ({ commentableId, body }) => {
        return {
          url: `/commentables/${commentableId}/comments`,
          method: "post",
          data: { body: body },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Comment"],
    }),
    deleteComment: builder.mutation({
      query: ({ id, commentableId, body }) => {
        return {
          url: `/commentables/${commentableId}/comments/${id}`,
          method: "delete",
          data: { body: body, commentable_id: commentableId },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: ["Comment"],
    }),
    markUnreadCommentNotification: builder.mutation({
      query: ({ notification_id }) => {
        return {
          url: `/notifications/unread_comment`,
          method: "put",
          data: { notification_id },
        };
      },
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => [{ type: "Comment", id: "LIST" }, "Comment"],
    }),

    // PROTOTYPE PDF COMBINE

    getCombinePdfsPrototype: builder.query({
      query: ({ opportunityId }) => ({
        url: `/combined_pdfs?opportunity_id=${opportunityId}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Opportunity"],
    }),

    /*
    // ADMIN
    */

    // ACCOUNTS
    getAdminAccounts: builder.query({
      query: () => ({ url: `/admin/accounts`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "AdminAccount", id })),
        { type: "AdminAccount", id: "LIST" },
      ],
    }),
    getAdminAccount: builder.query({
      query: (id) => ({ url: `/admin/accounts/${id}`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "AdminAccount", id }],
    }),
    updateAdminAccount: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/admin/accounts/${id}`,
        method: "patch",
        data: { account: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          { type: "AdminAccount", id: id },
          { type: "AdminAccount", id: "LIST" },
        ];
      },
    }),

    // ACCOUNT REVIEWS
    createAdminAccountReview: builder.mutation({
      query: ({ accountId, ...body }) => ({
        url: `/admin/accounts/${accountId}/review`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: ({ id }) => [
        // "AdminAccount",
        // { type: "AdminAccount", id: "LIST" },
        { type: "AdminAccount", id: id },
      ],
    }),

    // ADMIN CONTACTS
    getAdminContacts: builder.query({
      query: ({ page = 1, limit = 10, search }) => {
        if (search && search.length > 0) {
          return {
            url: `/admin/contacts?page=${page}&limit=${limit}&search=${search}`,
            method: "get",
          };
        } else {
          return {
            url: `/admin/contacts?page=${page}&limit=${limit}`,
            method: "get",
          };
        }
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        return [
          ...result.contacts.data.map(({ id }) => ({ type: "Contact", id })),
          { type: "Contact", id: "LIST" },
        ];
      },
    }),
    createAdminContact: builder.mutation({
      query: ({ orgAdmin, ...body }) => ({
        url: `/admin/contacts`,
        method: "post",
        data: {
          orgAdmin: orgAdmin,
          contact: body,
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Contact", id: "LIST" }, "Contact"],
    }),
    updateAdminContact: builder.mutation({
      query: ({ contactId, ...body }) => ({
        url: `/admin/contacts/${contactId}`,
        method: "patch",
        data: { contact: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Contact", id: "LIST" }, "Contact"],
    }),
    getAdmins: builder.query({
      query: () => ({
        url: `/admins`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        return [
          ...result.map(({ id }) => ({ type: "Contact", id })),
          { type: "Contact", id: "LIST" },
        ];
      },
    }),
    addAdmin: builder.mutation({
      query: ({ ids, notifyContacts, notifMsg, ...body }) => ({
        url: `/admins`,
        method: "post",
        data: {
          contact_ids: ids,
          notify_contacts: notifyContacts,
          notif_msg: notifMsg,
          contact: body,
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Contact", id: "LIST" }, "Contact"],
    }),
    revokeAdmins: builder.mutation({
      query: ({ ids }) => ({
        url: `/admins/revoke`,
        method: "delete",
        data: { contact_ids: ids },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Contact", id: "LIST" }, "Contact"],
    }),

    // FINANCING OPTION APPROVE
    createAdminFinancingOptionApproval: builder.mutation({
      query: ({ financingOptionId, approvalStatus, ...body }) => ({
        url: `/admin/financing_options/${financingOptionId}/approve`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { 
          approval_status: approvalStatus || 'approved', // Default to full approval
          ...body 
        },
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: ({ id }) => [
        { type: "FinancingOption", id: id },
        "Opportunity",
        "AdminAccount",
        "Task",
        "Transaction",
        { type: "Transaction", id: "LIST" },
      ],
    }),

    removeAdminFinancingOptionApproval: builder.mutation({
      query: ({ financingOptionId, ...body }) => ({
        url: `/admin/financing_options/${financingOptionId}/approve`,
        method: "delete",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: ({ id }) => [
        { type: "FinancingOption", id: id },
        "Opportunity",
        "AdminAccount",
        "Task",
        "Transaction",
        { type: "Transaction", id: "LIST" },
      ],
    }),

    //TAGS
    getTags: builder.query({
      query: () => ({
        url: `/tags`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: () => ["Tag"],
    }),
    createTag: builder.mutation({
      query: ({ ...body }) => ({
        url: `/tags`,
        method: "post",
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "Opportunity", id: "LIST" },
        "Opportunity",
        "Tag",
        "Task",
      ],
    }),
    deleteTags: builder.mutation({
      query: ({ ...body }) => ({
        url: `/tags`,
        method: "delete",
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "Opportunity", id: "LIST" },
        "Opportunity",
        "Tag",
        "Task",
      ],
    }),
    updateTagOpportunities: builder.mutation({
      query: ({ ...body }) => ({
        url: `/tags`,
        method: "put",
        data: { ...body },
      }),
      transformResponse: (response) => {
        // For single_remove, preserve the full response including 'included'
        if (response.data && response.included) {
          return {
            ...response.data,
            included: response.included
          };
        }
        return response.data;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // For single_remove, manually update the opportunity cache
          if (arg.single_remove && data) {
            dispatch(
              fortApi.util.updateQueryData('getOpportunity', arg.opportunity_id, (draft) => {
                // Update the opportunity data with the response
                if (data.attributes) {
                  Object.assign(draft.attributes, data.attributes);
                }
                // Update included tags from the response
                if (data.included !== undefined) {
                  draft.included = data.included;
                }
              })
            );
          }
        } catch (error) {
          console.error('Error updating tag cache:', error);
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Opportunity", id: arg.opportunity_id },
        { type: "Opportunity", id: "LIST" },
        { type: "AdminOpportunity", id: "LIST" },
        { type: "OpportunityTags", id: arg.opportunity_id },
        "AdminOpportunity",
        "Opportunity",
        "Tag",
      ],
    }),

    // SAVED VIEWS
    getSavedViews: builder.query({
      query: () => ({
        url: `/saved_views`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        return [
          ...result.map(({ id }) => ({ type: "SavedView", id })),
          { type: "SavedView", id: "LIST" },
        ];
      },
    }),
    createSavedView: builder.mutation({
      query: ({ ...body }) => ({
        url: `/saved_views`,
        method: "post",
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      // invalidatesTags: [
      //   { type: "SavedView", id: "LIST" },
      //   "SavedView",
      // ],
    }),
    updateSavedView: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/saved_views/${id}`,
        method: "patch",
        data: { id, ...body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => [
        { type: "SavedView", id: id },
        { type: "SavedView", id: "LIST" },
        "SavedView",
        { type: "Opportunity", id: "LIST" },
        "Task",
      ],
    }),
    deleteSavedView: builder.mutation({
      query: ({ id }) => ({
        url: `/saved_views/${id}`,
        method: "delete",
        data: { id },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["SavedView", { type: "SavedView", id: "LIST" }],
    }),

    // OPPORTUNITIES
    getAdminOpportunities: builder.query({
      query: ({ page, limit, filters, sort, search }) => {
        let url = `/admin/opportunities`;

        let queries = [];

        if (page) {
          queries.push(`page=${page}`);
        }
        if (limit) {
          queries.push(`limit=${limit}`);
        }
        if (filters && filters.length > 0) {
          queries.push(`filters=${filters}`);
        }
        if (search && search.length > 0) {
          queries.push(`search=${search}`);
        }
        if (sort) {
          queries.push(`sort=${sort}`);
        }
        url = url + "?";
        for (let i = 0; i < queries.length; i++) {
          url = url + queries[i];
          if (i != queries.length - 1) {
            url = url + "&";
          }
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        return [
          ...result.opportunities.data.map(({ id }) => ({
            type: "AdminOpportunity",
            id,
          })),
          { type: "AdminOpportunity", id: "LIST" },
        ];
      },
    }),
    deleteAdminOpportunities: builder.mutation({
      query: ({ filters, search, originator, opportunityIds }) => {
        let url = "/admin/opportunities";
        let queries = [];

        if (filters && filters.length > 0) {
          queries.push(`filters=${filters}`);
        }
        if (search && search.length > 0) {
          queries.push(`search=${search}`);
        }
        if (originator && originator.length > 0) {
          queries.push(`originator=${originator}`);
        }
        if (opportunityIds && opportunityIds.length > 0) {
          let opportunityQuery = `opportunity_ids=`;
          for (let i = 0; i < opportunityIds.length; i++) {
            opportunityQuery = opportunityQuery + opportunityIds[i];
            if (i != opportunityIds.length - 1) {
              opportunityQuery = opportunityQuery + ",";
            }
          }
          queries.push(opportunityQuery);
        }
        url = url + "?";
        for (let i = 0; i < queries.length; i++) {
          url = url + queries[i];
          if (i != queries.length - 1) {
            url = url + "&";
          }
        }

        return { url: url, method: "delete" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        "AdminOpportunity",
        { type: "AdminOpportunity", id: "LIST" },
      ],
    }),

    // CLIENT OPPORTUNITIES
    getAdminClientOpportunities: builder.query({
      query: ({ clientId, page, limit, filters, sort, search }) => {
        let url = `/admin/clients/${clientId}/opportunities`;
        let queries = [];
        if (page) {
          queries.push(`page=${page}`);
        }
        if (limit) {
          queries.push(`limit=${limit}`);
        }
        if (filters && filters.length > 0) {
          queries.push(`filters=${filters}`);
        }
        if (search && search.length > 0) {
          queries.push(`search=${search}`);
        }
        if (sort) {
          queries.push(`sort=${sort}`);
        }
        url = url + "?";
        for (let i = 0; i < queries.length; i++) {
          url = url + queries[i];
          if (i != queries.length - 1) {
            url = url + "&";
          }
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        return [
          ...result.opportunities.data.map(({ id }) => ({
            type: "AdminOpportunity",
            id,
          })),
          { type: "AdminOpportunity", id: "LIST" },
        ];
      },
    }),

    // VENDOR OPPORTUNITIES
    getAdminVendorOpportunities: builder.query({
      query: ({ vendorId, page, limit, filters, sort, search }) => {
        let url = `/admin/vendors/${vendorId}/opportunities`;
        let queries = [];
        if (page) {
          queries.push(`page=${page}`);
        }
        if (limit) {
          queries.push(`limit=${limit}`);
        }
        if (filters && filters.length > 0) {
          queries.push(`filters=${filters}`);
        }
        if (search && search.length > 0) {
          queries.push(`search=${search}`);
        }
        if (sort) {
          queries.push(`sort=${sort}`);
        }
        url = url + "?";
        for (let i = 0; i < queries.length; i++) {
          url = url + queries[i];
          if (i != queries.length - 1) {
            url = url + "&";
          }
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        const opportunities = response.data.map((opportunity) => ({
          id: opportunity.id,
          attributes: opportunity.attributes,
          relationships: opportunity.relationships,
        }));

        const included = response.included || [];

        const meta = response.meta;

        return { opportunities, included, meta };
      },
      providesTags: (result) => {
        return [
          ...result.opportunities.map(({ id }) => ({
            type: "AdminOpportunity",
            id,
          })),
          { type: "AdminOpportunity", id: "LIST" },
        ];
      },
    }),

    // VENDOR PROGRAMS
    getAdminVendorPrograms: builder.query({
      query: ({ vendorId, page, limit, search }) => {
        if (vendorId) {
          return {
            url: `/admin/vendor_programs?vendor_id=${vendorId}`,
            method: "get",
          };
        }
        let url = `/admin/vendor_programs`;
        let queries = [];

        if (page) {
          queries.push(`page=${page}`);
        }
        if (limit) {
          queries.push(`limit=${limit}`);
        }
        if (search && search.length > 0) {
          queries.push(`search=${search}`);
        }
        url = url + "?";
        for (let i = 0; i < queries.length; i++) {
          url = url + queries[i];
          if (i != queries.length - 1) {
            url = url + "&";
          }
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [
        { type: "VendorProgram", id },
        { type: "VendorProgram", id: "LIST" },
        { type: "AdminVendorProgram", id: "LIST" },
      ],
    }),

    getAdminVendorProgramTemplates: builder.query({
      query: ({ id, template }) => {
        return {
          url: `/admin/vendor_programs/templates?id=${id}&template=${template}`,
          method: "get",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "AdminVendorProgram", id })),
        { type: "Document", id: "LIST" },
        { type: "AdminVendorProgram", id: "LIST" },
        { type: "FinancingOptionTemplate", id: "LIST" },
        { type: "OpportunityEnvelopeTemplate", id: "LIST" },
      ],
    }),

    getAdminVendorProgram: builder.query({
      query: (id) => ({ url: `/admin/vendor_programs/${id}`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "AdminVendorProgram", id }],
    }),
    updateAdminVendorProgram: builder.mutation({
      query: ({
        id,
        attachment_set,
        allowed_finance_types,
        attachment,
        ...patch
      }) => ({
        url: `/admin/vendor_programs/${id}`,
        method: "patch",
        data: {
          vendor_program: patch,
          attachment: attachment,
          attachment_set: attachment_set,
          allowed_finance_types: allowed_finance_types,
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          "Opportunity",
          { type: "AdminVendorProgram", id: id },
          { type: "AdminVendorProgram", id: "LIST" },
          "Task",
        ];
      },
    }),

    createAdminVendorProgram: builder.mutation({
      query: ({ ...body }) => ({
        url: `/admin/vendor_programs`,
        method: "post",
        data: { vendor_program: body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          "Opportunity",
          { type: "VendorProgram", id: id },
          { type: "VendorProgram", id: "LIST" },
          { type: "AdminVendorProgram", id: id },
          { type: "AdminVendorProgram", id: "LIST" },
          "Task",
        ];
      },
    }),
    createVendorProgramEnvelopeTemplate: builder.mutation({
      query: ({ id, document_ids, ...body }) => ({
        url: `/admin/vendor_programs/create_envelope_template`,
        method: "post",
        data: {
          id: id,
          document_ids: document_ids,
          envelope_template: body,
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [
          { type: "AdminVendorProgram", id: id },
          { type: "FinancingOptionTemplate", id: "LIST" },
          { type: "OpportunityEnvelopeTemplate", id: "LIST" },
        ];
      },
    }),
    deleteVendorProgramTemplates: builder.mutation({
      query: ({ id, templateIds, which_template }) => {
        let url = `/admin/vendor_programs/delete_templates`;
        url = `${url}?id=${id}&which_template=${which_template}&template_ids=${templateIds}`;
        return { url: url, method: "delete" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }, error, { templateIds }) => {
        return [
          ...templateIds.split(",").map(({ id }) => ({
            type: "OpportunityEnvelopeTemplate",
            id,
          })),
          { type: "AdminVendorProgram", id: id },
          { type: "FinancingOptionTemplate", id: "LIST" },
          { type: "OpportunityEnvelopeTemplate", id: "LIST" },
        ];
      },
    }),
    deleteVendorProgramAttachment: builder.mutation({
      query: ({ id }) => {
        return {
          url: `/admin/vendor_programs/delete_attachment?id=${id}`,
          method: "delete",
        };
      },
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ({ id }) => {
        return [{ type: "AdminVendorProgram", id: id }];
      },
    }),

    // ACCOUNT TEAMS
    getAdminAccountTeams: builder.query({
      query: (vendorId) => ({
        url: `/admin/accounts/${vendorId}/teams`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Team", id })),
        { type: "Team", id: "LIST" },
      ],
    }),

    // USER TEAMS
    getAdminUserTeams: builder.query({
      query: (contact) => ({
        url: `/admin/contacts/${contact}/teams`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Team", id })),
        { type: "Team", id: "LIST" },
      ],
    }),

    // TEAMS
    getAdminTeams: builder.query({
      query: () => ({ url: `/admin/teams`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Team", id })),
        { type: "Team", id: "LIST" },
      ],
    }),
    getAdminTeam: builder.query({
      query: ({ id }) => ({
        url: `/admin/teams/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, { id }) => [{ type: "Team", id }],
    }),
    searchAdminTeams: builder.query({
      query: ({ search }) => {
        let url = `/admin/teams/search`;
        if (search && search.length > 0) {
          url = url + "?search=" + search;
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Team", id })),
        { type: "Team", id: "LIST" },
      ],
    }),

    createAdminTeam: builder.mutation({
      query: ({ vendor_id, team_name }) => ({
        url: `/admin/teams`,
        method: "post",
        data: {
          team: {
            name: team_name,
            account_id: vendor_id,
            created_from_account_id: vendor_id,
          },
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Team", id: "LIST" }],
    }),

    updateAdminTeam: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/admin/teams/${id}`,
        method: "patch",
        data: { team: { ...body } },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: (result, error, { id }) => [{ type: "Team", id }, { type: "Team", id: "LIST" }],
    }),

    deleteAdminTeamLogo: builder.mutation({
      query: ({ id }) => ({
        url: `/admin/teams/${id}/delete_logo`,
        method: "delete",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: (result, error, { id }) => [{ type: "Team", id }, { type: "Team", id: "LIST" }],
    }),

    // VENDOR ADMINS
    getAdminVendorAdmins: builder.query({
      query: (vendorId) => ({
        url: `/admin/vendors/${vendorId}/admins`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        return [
          ...result.map(({ id }) => ({ type: "Contact", id })),
          { type: "Contact", id: "LIST" },
        ];
      },
    }),
    createAdminVendorAdmin: builder.mutation({
      query: ({ vendorId, ...body }) => ({
        url: `/admin/vendors/${vendorId}/admins`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: body,
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [
        { type: "VendorContact", id: "LIST" },
        "VendorContact",
        "Vendor",
        "Contact",
      ],
    }),
    updateAdminVendorAdmins: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/admin/vendors/${id}/admins`,
        method: "patch",
        data: { admin: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: () => [{ type: "Team", id: "LIST" }],
    }),

    // VENDOR TEAMS
    updateAdminVendorTeams: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/admin/vendors/${id}/teams`,
        method: "patch",
        data: { team: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: () => [{ type: "Team", id: "LIST" }],
    }),

    // UPDATE VENDOR VENDOR PROGRAM
    updateAdminVendorVendorProgram: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/admin/vendors/${id}`,
        method: "patch",
        data: { ...patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: () => [
        "Vendor",
        { type: "Vendor", id: "LIST" },
        { type: "VendorProgram", id: "LIST" },
        { type: "AdminVendorProgram", id: "LIST" },
      ],
    }),

    // MEMBERSHIPS

    getMembershipsByContact: builder.query({
      query: (contactId) => ({
        url: `/contacts/${contactId}/memberships`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Membership", id })),
        { type: "Membership", id: "LIST" },
      ],
    }),

    getMembershipsByTeam: builder.query({
      query: (team_id) => ({
        url: `/teams/${team_id}/memberships`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Membership", id })),
        { type: "Membership", id: "LIST" },
      ],
    }),
    inviteMembershipsByTeam: builder.mutation({
      query: ({ teamId, ...body }) => ({
        url: `/teams/${teamId}/memberships`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: body,
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: (result) => [
        { type: "Membership", id: result.id },
        { type: "Membership", id: "LIST" },
        "Membership",
      ],
    }),
    updateAdminMembershipRole: builder.mutation({
      query: ({ team_id, memberId, ...body }) => ({
        url: `/admin/teams/${team_id}/memberships/${memberId}`,
        method: "put",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: body,
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: (result) => [{ type: "Membership", id: result.id }],
    }),

    deleteAdminMembership: builder.mutation({
      query: ({ team_id, memberId, ...body }) => ({
        url: `/admin/teams/${team_id}/memberships/${memberId}`,
        method: "DELETE",
        data: body,
      }),
      invalidatesTags: () => [{ type: "Membership", id: "LIST" }, "Membership"],
    }),

    getMembershipsByAdminTeam: builder.query({
      query: (team_id) => ({
        url: `/admin/teams/${team_id}/memberships`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Membership", id })),
        { type: "Membership", id: "LIST" },
      ],
    }),
    createMembershipsByTeam: builder.mutation({
      query: ({ teamId, contactId }) => ({
        url: `/admin/teams/${teamId}/memberships`,
        method: "post",
        data: { body: { contact_id: contactId } },
      }),
      transformResponse: (response) => {
        return response;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Membership", id })),
        { type: "Membership", id: "LIST" },
      ],
      invalidatesTags: () => ["Membership"],
    }),

    /*
    // VENDOR ADMIN
    */

    // TEAMS
    getVendorAdminTeams: builder.query({
      query: () => ({ url: `/vendor_admin/teams`, method: "get" }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Team", id })),
        { type: "Team", id: "LIST" },
      ],
    }),
    getVendorAdminTeam: builder.query({
      query: ({ id }) => ({
        url: `/vendor_admin/teams/${id}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: "Team", id }],
    }),
    createVendorAdminTeam: builder.mutation({
      query: ({ vendor_id, team_name }) => ({
        url: `/vendor_admin/teams`,
        method: "post",
        data: {
          team: {
            name: team_name,
            account_id: vendor_id,
            created_from_account_id: vendor_id,
          },
        },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Team", id: "LIST" }],
    }),

    // MEMBERSHIPS
    getVendorAdminMembershipsByTeam: builder.query({
      query: (team_id) => ({
        url: `/vendor_admin/teams/${team_id}/memberships`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Membership", id })),
        { type: "Membership", id: "LIST" },
      ],
    }),
    updateVendorAdminMembershipRole: builder.mutation({
      query: ({ team_id, memberId, ...body }) => ({
        url: `/vendor_admin/teams/${team_id}/memberships/${memberId}`,
        method: "put",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: body,
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Membership", id: "LIST" }, "Membership"],
    }),

    // VENDOR ADMIN LIST

    getVendorAdmins: builder.query({
      query: ({ vendorId }) => ({
        url: `/vendor_admin/vendors?vendorId=${vendorId}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        return [
          ...result.map(({ id }) => ({ type: "Contact", id })),
          { type: "Contact", id: "LIST" },
        ];
      },
    }),

    addContactsAsVendorAdmin: builder.mutation({
      query: ({ vendorId, contactId }) => ({
        url: `/vendor_admin/vendors/${vendorId}`,
        method: "put",
        data: {
          vendorId: vendorId,
          contact_id: contactId,
        },
      }),
      transformResponse: (response) => {
        return response;
      },
      providesTags: (result) => [
        ...result.map(({ id }) => ({ type: "Contact", id })),
        { type: "Contact", id: "LIST" },
      ],
      invalidatesTags: () => ["Contact"],
    }),

    createVendorAdminContact: builder.mutation({
      query: ({ vendorId, ...body }) => ({
        url: `/vendor_admin/vendors/${vendorId}/contacts`,
        method: "post",
        headers: {
          "content-type": "multipart/form-data",
        },
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: [{ type: "Membership", id: "LIST" }, "Membership"],
    }),

    // CREDIT REVIEWS
    createCreditReview: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/credit_reviews/create`,
        method: "post",
        data: { ...body },
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Opportunity", "Task"],
    }),

    // HUBSPOT UPDATE
    pushHubspotUpdate: builder.mutation({
      query: ({ opportunityId }) => ({
        url: `/opportunities/${opportunityId}/hubspot_updates/push`,
        method: "patch",
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["Opportunity", "Task"],
    }),

    // GOOGLE DRIVE FOLDER
    createGoogleDriveFolder: builder.mutation({
      query: ({ opportunityId, clientId, vendorId, contactId, lenderId, ...body }) => {
        let url;
        
        if (opportunityId) {
          url = `/opportunities/${opportunityId}/google_drive_folder`;
        } else if (clientId) {
          url = `/clients/${clientId}/google_drive_folder`;
        } else if (vendorId) {
          url = `/vendors/${vendorId}/google_drive_folder`;
        } else if (contactId) {
          url = `/contacts/${contactId}/google_drive_folder`;
        } else if (lenderId) {
          url = `/lenders/${lenderId}/google_drive_folder`;
        } else {
          throw new Error('No valid entity ID provided for Google Drive folder creation');
        }
        
        return {
          url: url,
          method: "post",
          data: { ...body },
        };
      },
      transformResponse: (response) => {
        return response;
      },
    }),

    // PAYMENT LINK SANDBOX

    createPaymentLink: builder.mutation({
      query: (body) => ({
        url: `/payment_links`,
        method: "post",
        data: { payment_link: body },
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: () => ["PaymentLink"],
    }),

    // STRIPE CLIENT SECRET

    getStripeSetupIntent: builder.query({
      query: ({ contactId, vendorId, opportunityId }) => ({
        url: `/stripe_payments/${contactId}/setup_intents/${vendorId}?opportunity_id=${opportunityId}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        if (result?.id) {
          return [{ type: "StripeSetupIntent", id: result.id }];
        } else {
          return [];
        }
      },
    }),

    getStripeSetupIntents: builder.query({
      query: ({ contactId, vendorId }) => ({
        url: `/stripe_payments/${contactId}/setup_intents${
          vendorId ? `?vendor_id=${vendorId}` : ""
        }`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        if (result?.id) {
          return [{ type: "StripeSetupIntent", id: result.id }];
        } else {
          return [];
        }
      },
    }),

    createStripeSetupIntent: builder.mutation({
      query: ({ contactId, vendorId, opportunityId, country }) => ({
        url: `/stripe_payments/${contactId}/setup_intents?vendor_id=${vendorId}&opportunity_id=${opportunityId}`,
        method: "post",
        data: { country },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: () => [
        "StripeSetupIntent",
        "StripePaymentMethod",
        "Opportunity",
        "Task",
      ],
    }),

    updateStripeCustomerReference: builder.mutation({
      query: ({ contactId, vendorId, ...body }) => ({
        url: vendorId
          ? `/stripe_payments/${contactId}/setup_intents/${vendorId}`
          : `/stripe_payments/${contactId}/setup_intents/null`,
        method: "patch",
        data: { stripe_customer_reference: { ...body } },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: () => [
        "StripeSetupIntent",
        "StripePaymentMethod",
        "Task",
      ],
    }),

    validateStripeCustomerReference: builder.mutation({
      query: ({ contactId, vendorId }) => ({
        url: `/stripe_payments/${contactId}/setup_intents/${vendorId}/validate`,
        method: "patch",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: () => [
        "StripeSetupIntent",
        "StripePaymentMethod",
        "Task",
      ],
    }),

    getStripeListAllPaymentMethods: builder.query({
      query: ({ contactId, vendorId, onClientPortal }) => {
        let url = `/stripe_payments/${contactId}/payment_methods`;
        let params = [];

        if (vendorId) {
          params.push(`vendor_id=${vendorId}`);
        }

        if (onClientPortal) {
          params.push(`on_client_portal=${onClientPortal}`);
        }

        if (params.length > 0) {
          url += `?${params.join("&")}`;
        }
        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: () => {
        return ["StripePaymentMethod"];
      },
    }),

    getStripeListPaymentMethods: builder.query({
      query: ({ contactId, vendorId }) => ({
        url: `/stripe_payments/${contactId}/payment_methods/${vendorId}`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: () => {
        return ["StripePaymentMethod"];
      },
    }),

    cloneStripePaymentMethods: builder.mutation({
      query: ({ contactId, vendorId }) => ({
        url: `/stripe_payments/${contactId}/setup_intents/${vendorId}/clone`,
        method: "post",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: () => [
        "StripeSetupIntent",
        "StripePaymentMethod",
        "Opportunity",
        "Task",
      ],
    }),

    // OPENAI
    createChatOpenai: builder.mutation({
      query: ({ report_text }) => ({
        url: `/openai/create_chat`,
        data: { report_text },
        method: "post",
      }),
      transformResponse: (response) => {
        return response;
      },
    }),

    // CREDIT PREVIEW
    getCreditPreview: builder.query({
      query: ({ opportunityId, creditPreviewId }) => {
        let url = "";

        if (creditPreviewId) {
          url = `/credit_previews/${creditPreviewId}`;
        } else if (opportunityId) {
          url = `/opportunities/${opportunityId}/credit_previews`;
        }

        return { url: url, method: "get" };
      },
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: (result) => {
        if (result?.id) {
          return ["CreditPreview", { type: "CreditPreview", id: result.id }];
        } else {
          return ["CreditPreview"];
        }
      },
    }),

    createCreditPreview: builder.mutation({
      query: ({ opportunityId, ...body }) => ({
        url: `/opportunities/${opportunityId}/credit_previews`,
        method: "post",
        data: { credit_preview: { ...body } },
      }),
      transformResponse: (response) => {
        return response;
      },
      invalidatesTags: (result) => {
        if (result?.id) {
          return ["CreditPreview", { type: "CreditPreview", id: result.id }];
        } else {
          return ["CreditPreview"];
        }
      },
    }),

    createOpportunityCsv: builder.mutation({
      query: ({ contact_id, opportunityIds }) => ({
        url: `/csv_exports/opportunities`,
        data: { contact_id: contact_id, opportunityIds: opportunityIds },
        method: "post",
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),
    createTransactionCsv: builder.mutation({
      query: ({ status, search }) => ({
        url: `/csv_exports/transactions`,
        data: { status, search },
        method: "post",
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),
    createCreditSubmissionCsv: builder.mutation({
      query: ({ creditSubmissionIds }) => ({
        url: `/csv_exports/credit_submissions`,
        data: { creditSubmissionIds },
        method: "post",
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),
    createContactCsv: builder.mutation({
      query: () => ({
        url: `/csv_exports/contacts`,
        method: "post",
      }),
      transformResponse: (response) => {
        return response.data;
      },
    }),

    getRateCards: builder.query({
      query: (lenderId) => ({
        url: `/lenders/${lenderId}/rate_cards`,
        method: "get",
      }),
      transformResponse: (response) => {
        return response.data;
      },
      providesTags: () => {
        return ["RateCard"];
      },
    }),
    createRateCard: builder.mutation({
      query: ({ lenderId, ...post }) => ({
        url: `/lenders/${lenderId}/rate_cards`,
        method: "post",
        data: { rate_card: post },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["RateCard"],
    }),
    updateRateCard: builder.mutation({
      query: ({ lenderId, id, ...patch }) => ({
        url: `/lenders/${lenderId}/rate_cards/${id}`,
        method: "patch",
        data: { rate_card: patch },
      }),
      transformResponse: (response) => {
        return response.data;
      },
      invalidatesTags: ["RateCard"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetClientStatsQuery,

  useCreatePaymentLinkMutation,
  useGetStripeSetupIntentQuery,
  useGetStripeSetupIntentsQuery,
  useCreateStripeSetupIntentMutation,
  useUpdateStripeCustomerReferenceMutation,
  useValidateStripeCustomerReferenceMutation,
  useGetStripeListAllPaymentMethodsQuery,
  useGetStripeListPaymentMethodsQuery,
  useCloneStripePaymentMethodsMutation,

  // OPENAI
  useCreateChatOpenaiMutation,

  // CREDIT PREVIEW
  useCreateCreditPreviewMutation,
  useGetCreditPreviewQuery,

  useGetOpportunitiesQuery,
  useLazyGetOpportunitiesQuery,

  useGetOpportunityStatsQuery,

  useGetOpportunityQuery,
  useCreateOpportunityMutation,
  useUpdateOpportunityMutation,
  useUpdateOpportunityStageMutation,
  useDeleteOpportunitiesMutation,
  useRequestOpportunityAccessMutation,

  useCreateOpportunityAttachmentMutation,

  useGetOpportunityAttachmentsQuery,
  useCreateOpportunityAttachmentsMutation,
  useUpdateOpportunityAttachmentMutation,
  usePurgeOpportunityAttachmentMutation,
  useDeleteOpportunityAttachmentMutation,
  // useCreateOpportunityDocumentsMutation,

  useGetOpportunityEnvelopeTemplatesQuery,
  useCreateOpportunityEnvelopeTemplateMutation,
  useUpdateOpportunityEnvelopeTemplateMutation,
  useDeleteOpportunityEnvelopeTemplateMutation,
  useGenerateOpportunityEnvelopeTemplateMutation,
  useGenerateOpportunityEnvelopeTemplatesMutation,

  useGetOpportunityEventsQuery,
  useCreateOpportunityEventMutation,
  useCreateOpportunityCsvMutation,
  useCreateTransactionCsvMutation,
  useCreateCreditSubmissionCsvMutation,
  useCreateContactCsvMutation,

  useGetCompaniesSearchQuery,

  useGetContactQuery,
  useGetContactsQuery,
  useSearchContactsQuery,
  useFetchContactQuery,

  useCreateContactMutation,
  useCaptureContactMutation,
  useUpdateContactMutation,

  useGetOpportunityContactsQuery,
  useCreateOpportunityContactMutation,
  useUpdateOpportunityContactMutation,
  useDeleteOpportunityContactsMutation,
  useCaptureOpportunityContactMutation,

  useCreateDesignatedSignerMutation,

  useSearchOpportunityOriginatorsQuery,
  useUpdateOpportunityOriginatorMutation,

  useGetAccountQuery,
  useGetAccountsQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,

  useGetAccountEventsQuery,

  useGetClientQuery,
  useGetClientsQuery,

  useGetClientOpportunitiesQuery,

  useGetClientAccountQuery,
  useGetClientAccountsQuery,
  useUpdateClientAccountMutation,

  useGetTeamAccountsQuery,
  useGetAccountTeamsQuery,
  useGetTeamsQuery,
  useGetTeamQuery,
  useUpdateTeamMutation,

  useGetTeamClientsQuery,
  useCreateClientMutation,
  useUpdateClientMutation,

  useCreateTeamContactMutation,
  useSearchTeamContactsQuery,

  useGetFinancingOptionsQuery,
  useGetFinancingOptionQuery,
  useGetNewFinancingOptionQuery,
  useCreateFinancingOptionMutation,
  useUpdateFinancingOptionMutation,
  useDeleteFinancingOptionMutation,

  useCreateFinancingOptionEventMutation,

  useGetFinancingOptionSegmentsQuery,

  useGetFinancingOptionTemplatesQuery,
  useCreateFinancingOptionTemplateMutation,
  useUpdateFinancingOptionTemplateMutation,

  useGetAdminCreditSubmissionsQuery,

  useGetOpportunityCreditSubmissionsQuery,
  useGetOpportunityCreditSubmissionQuery,
  useCreateOpportunityCreditSubmissionMutation,
  useUpdateOpportunityCreditSubmissionMutation,
  useDeleteOpportunityCreditSubmissionMutation,
  useUpdateOpportunityCreditSubmissionSubmitToLenderMutation,
  useUpdateOpportunityCreditSubmissionSelectLenderMutation,
  useUpdateOpportunityCreditSubmissionDeselectLenderMutation,

  useGetVendorsQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,

  useGetVendorPaymentsQuery,
  useGetVendorPaymentQuery,
  useCreateVendorPaymentMutation,
  useUpdateVendorPaymentMutation,
  useDeleteVendorPaymentMutation,

  useGetVendorPaymentAttachmentQuery,
  useCreateVendorPaymentAttachmentMutation,

  useGenerateVendorPaymentMutation,
  useCreateVendorPaymentConfirmationMutation,

  useCreateVendorPaymentOpportunityMutation,

  useGetVendorProgramQuery,
  useGetVendorProgramsQuery,

  useCreateDocusignEmbeddedSigningMutation,
  useGetDocusignEmbeddedSigningStatusQuery,
  useCreateDocusignEmbeddedSendingMutation,
  useGetDocusignPdfsForSigningQuery,

  useGetDocusignRegeneratedEmbeddedSendingUrlQuery,
  useLazyGetDocusignRegeneratedEmbeddedSendingUrlQuery,

  useGetDocusignSigningUrlQuery,
  useLazyGetDocusignSigningUrlQuery,

  useGetLendersQuery,
  useCreateLenderMutation,

  useGetOpportunityDocumentsQuery,
  useGetDocumentsQuery,
  useGetDocumentQuery,
  useCreateDocumentMutation,
  useUpdateDocumentMutation,
  usePreviewDocumentMutation,

  useGetGuarantorsQuery,
  useGetGuarantorQuery,
  useSearchGuarantorsQuery,
  useCreateGuarantorMutation,
  useRemoveGuarantorsMutation,

  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,

  useGetOpportunityTransactionsQuery,
  useGetOpportunityTransactionQuery,
  useCreateOpportunityTransactionMutation,
  useUpdateOpportunityTransactionMutation,
  useUpdateOpportunityTransactionsMutation,
  useDeleteOpportunityTransactionMutation,
  useDeleteOpportunityTransactionsMutation,
  useScheduleOpportunityTransactionsMutation,

  useGetOpportunityTasksQuery,

  useGetOpportunityTagsQuery,
  useUpdateOpportunityTagsMutation,

  // TODO: remove
  //
  // useCreateOpportunityDownPaymentMutation,

  useCreateOpportunityPaymentAuthorizationMutation,

  useGetDocusignEnvelopesQuery,
  useGetDocusignEnvelopeQuery,
  useCreateDocusignEnvelopeMutation,
  useUpdateDocusignEnvelopeMutation,
  useDeleteDocusignEnvelopeMutation,

  useGetCommentsQuery,
  useLazyGetCommentsQuery,
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useMarkUnreadCommentNotificationMutation,

  // PROTOTYPE
  useGetCombinePdfsPrototypeQuery,

  // ADMIN
  useGetAdminAccountsQuery,
  useGetAdminAccountQuery,
  useUpdateAdminAccountMutation,

  useCreateAdminAccountReviewMutation,

  useGetAdminClientOpportunitiesQuery,
  useGetAdminVendorOpportunitiesQuery,

  useGetAdminContactsQuery,
  useCreateAdminContactMutation,
  useUpdateAdminContactMutation,
  useGetAdminsQuery,
  useAddAdminMutation,
  useRevokeAdminsMutation,

  useCreateAdminFinancingOptionApprovalMutation,
  useRemoveAdminFinancingOptionApprovalMutation,

  // TAG
  useGetTagsQuery,
  useCreateTagMutation,
  useDeleteTagsMutation,
  useUpdateTagOpportunitiesMutation,

  useGetSavedViewsQuery,
  useCreateSavedViewMutation,
  useUpdateSavedViewMutation,
  useDeleteSavedViewMutation,

  useGetAdminOpportunitiesQuery,
  useDeleteAdminOpportunitiesMutation,

  useGetAdminVendorProgramsQuery,
  useGetAdminVendorProgramTemplatesQuery,
  useGetAdminVendorProgramQuery,
  useUpdateAdminVendorProgramMutation,

  useCreateAdminVendorProgramMutation,
  useDeleteVendorProgramTemplatesMutation,
  useDeleteVendorProgramAttachmentMutation,
  useCreateVendorProgramEnvelopeTemplateMutation,

  useGetAdminAccountTeamsQuery,

  useGetAdminUserTeamsQuery,

  useGetAdminTeamsQuery,
  useGetAdminTeamQuery,
  useSearchAdminTeamsQuery,
  useCreateAdminTeamMutation,
  useUpdateAdminTeamMutation,
  useDeleteAdminTeamLogoMutation,

  useGetAdminVendorAdminsQuery,
  useCreateAdminVendorAdminMutation,
  useUpdateAdminVendorAdminsMutation,

  useUpdateAdminVendorTeamsMutation,

  useUpdateAdminVendorVendorProgramMutation,

  useUpdateAdminMembershipRoleMutation,
  useDeleteAdminMembershipMutation,

  // VENDOR ADMIN
  useGetVendorAdminTeamsQuery,
  useGetVendorAdminTeamQuery,
  useGetVendorAdminMembershipsByTeamQuery,
  useGetVendorAdminsQuery,
  useAddContactsAsVendorAdminMutation,
  useCreateVendorAdminTeamMutation,
  useCreateVendorAdminContactMutation,
  useInviteMembershipsByTeamMutation,
  useUpdateVendorAdminMembershipRoleMutation,

  useGetMembershipsByTeamQuery,

  useGetMembershipsByAdminTeamQuery,
  useCreateMembershipsByTeamMutation,
  useGetMembershipsByContactQuery,

  useCreateCreditReviewMutation,

  usePushHubspotUpdateMutation,

  useGetRateCardsQuery,
  useCreateRateCardMutation,
  useUpdateRateCardMutation,

  useCreateGoogleDriveFolderMutation,
} = fortApi;

export { fortApiTagTypes };
